import {
    CreateDimensionInput,
    Dimension,
    UpdateDimensionInput
} from "../../../../API";
import {
    useMutation,
    useQuery,
    useQueryClient
} from "react-query";

import { Dimension as DimensionModel } from "../../../../models";
import { GraphQLDimensionDAOFactory } from "../../../../lib/dimension/dao/graphql/GraphQLDimensionDAOFactory";
import { ListResponse } from "../../../../lib/util/data/list/ListResponse";
import { useSnackbar } from "notistack";
import uuidv4 from "../../../../lib/util/UuidGenerator";

const graphQLDimensionDAO = GraphQLDimensionDAOFactory.getInstance();

export const useDimensions = (propertyId: string) => {
    const queryClient = useQueryClient();
    const snackbar = useSnackbar();

    const propertyDimensionQuery = useQuery({
        queryKey: ['dimensions', 'property', propertyId],
        queryFn: async () => {
            if (graphQLDimensionDAO.getDimensionsByPropertyId) {
                let allDimensions: DimensionModel[] = [];
                let nextToken: string | null | undefined = undefined;
                do {
                    const response: ListResponse<DimensionModel> = await graphQLDimensionDAO.getDimensionsByPropertyId(propertyId, nextToken);
                    allDimensions.push(...response.items);
                    nextToken = response.nextToken;
                } while (nextToken);
                return allDimensions;
            }
        }
    });

    const onEditDimensionSubmit = useMutation({
        mutationFn: async (dimension: UpdateDimensionInput) => {
            return await graphQLDimensionDAO.updateDimension(dimension.id, dimension) as Dimension;
        },
        onMutate: async (dimension: UpdateDimensionInput) => {
            const queryKey = ['dimensions', 'property', dimension.propertyId];
            await queryClient.cancelQueries(queryKey);
            const previousDimensions = queryClient.getQueryData(queryKey);
            queryClient.setQueryData(queryKey, (old: Array<Dimension> | undefined): Array<Dimension> => {
                return (old || []).map((oldDimension: Dimension) => {
                    if (oldDimension.id === dimension.id) {
                        // for any field that exists in the query but not in the update, we keep the old value
                        return {
                            ...oldDimension,
                            ...dimension,
                            measurement: {
                                ...oldDimension.measurement,
                                ...dimension.measurement,
                                __typename: "Measurement"
                            },
                            _version: (dimension._version ?? 0) + 1,
                        };
                    }
                    return oldDimension;
                });
            });
            return { previousDimensions };
        },
        onError: (error, variables, context) => {
            const queryKey = ['dimensions', 'property', variables.propertyId];
            snackbar.enqueueSnackbar("Failed to edit dimension", { variant: "error" });
            if (context?.previousDimensions) {
                queryClient.setQueryData(queryKey, context.previousDimensions);
            }
        },
        onSettled: (dimension) => {
            if (dimension?.propertyId) {
                queryClient.invalidateQueries(['dimensions', 'property', dimension.propertyId]);
            }
        }
    });

    const onCreateDimension = useMutation({
        mutationFn: async (dimension: CreateDimensionInput) => {
            const dimensionInput: DimensionModel = {
                id: uuidv4(),
                parentId: dimension.parentId,
                propertyId: dimension.propertyId,
                measurement: {
                    measurementUnit: dimension.measurement?.measurementUnit,
                    length: dimension.measurement?.length,
                    width: dimension.measurement?.width,
                    dimensionType: dimension.measurement?.dimensionType
                },
            };
            const createdDimension = await graphQLDimensionDAO.create(dimensionInput);
            return createdDimension as Dimension;
        },
        onMutate: async (dimension: CreateDimensionInput) => {
            const queryKey = ['dimensions', 'property', dimension.propertyId];
            await queryClient.cancelQueries(queryKey);
            const previousDimensions = queryClient.getQueryData(queryKey);
            queryClient.setQueryData(queryKey, (old: Array<Dimension> | undefined) => [...(old || []), dimension as Dimension]);
            return { previousDimensions: previousDimensions };
        },
        onError: (error, dimension, context: any) => {
            if (context?.previousDimensions) {
                const queryKey = ['dimensions', 'property', dimension.propertyId];
                queryClient.setQueryData(queryKey, context.previousDimensions);
            }
        },
        onSettled: (dimension) => {
            const queryKey = ['dimensions', 'property', dimension?.propertyId];
            queryClient.invalidateQueries(queryKey);
        }
    });

    return { propertyDimensionQuery, onEditDimensionSubmit, onCreateDimension };
};